// Plain-English text for the POS pages. The server sends codes; the owner
// reads these. A code with no entry here is shown humanized, never hidden.

import type { ReportBlockerCode, ReportStatus } from '../../api/pos-types.js';

export const STATUS_TEXT: Readonly<Record<ReportStatus, { label: string; detail: string }>> = {
  provisional: {
    label: 'Provisional',
    detail: 'Something is still missing or unmatched, so these figures may change.',
  },
  reconciled: {
    label: 'Reconciled',
    detail: 'Every shift is in and matched and nothing is outstanding. The day is not closed yet.',
  },
  final: {
    label: 'Final',
    detail: 'The day is closed and nothing is outstanding.',
  },
  corrected: {
    label: 'Corrected',
    detail:
      'The day is closed. A late record was posted as a correction; the original figures stand and the correction is shown beside them.',
  },
};

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export const BLOCKER_TEXT: Readonly<Record<ReportBlockerCode, (n: number) => string>> = {
  period_missing: () => 'Nothing has been received for this day yet.',
  shift_open: (n) => `${plural(n, 'shift is', 'shifts are')} still open.`,
  shift_unreconciled: (n) =>
    `${plural(n, 'closed shift has', 'closed shifts have')} not been compared with our figures yet.`,
  shift_mismatch: (n) =>
    `${plural(n, 'closed shift reports', 'closed shifts report')} figures that differ from ours.`,
  events_waiting: (n) => `${plural(n, 'record is', 'records are')} received but not applied yet.`,
  events_parked: (n) =>
    `${plural(n, 'record is', 'records are')} waiting for something they depend on.`,
  events_dead_lettered: (n) =>
    `${plural(n, 'record was', 'records were')} set aside for review, so the figures are missing ${n === 1 ? 'it' : 'them'}.`,
  payments_open: (n) =>
    `${plural(n, 'card or UPI payment is', 'card or UPI payments are')} still pending or unknown.`,
  payments_unclaimed: (n) =>
    `${plural(n, 'payment was', 'payments were')} collected that no bill records.`,
  refunds_awaiting_approval: (n) =>
    `${plural(n, 'refund is', 'refunds are')} waiting for your approval.`,
};

export const SETTLEMENT_STATE_TEXT: Readonly<Record<string, string>> = {
  open: 'Open',
  settled: 'Settled',
  zero_due: 'Nothing due',
  exception: 'Exception',
  credit_noted: 'Credit noted',
  written_off: 'Written off',
};

export const REFUND_REASON_TEXT: Readonly<Record<string, string>> = {
  credit_note: 'Credit note issued',
  duplicate_payment: 'Customer paid twice',
  late_payment: 'Customer paid after the till gave up',
  customer_request: 'Customer asked',
  other: 'Other',
};

export const MATCH_TEXT: Readonly<Record<string, string>> = {
  matched: 'Matches our records',
  amount_mismatch: 'Amount differs',
  not_succeeded: 'We hold it as not paid',
  unmatched: 'Not in our records',
};

export const TENDER_TEXT: Readonly<Record<string, string>> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  wallet: 'Wallet',
  aggregator_prepaid: 'Aggregator prepaid',
  other: 'Other',
};

export const textOr = (map: Readonly<Record<string, string>>, code: string): string =>
  map[code] ?? code.replace(/_/g, ' ');
